import { TextAreaWithLabel } from '@adotel/components'
import { useMutation } from '@apollo/client'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Typography from '@material-ui/core/Typography'
import { useState } from 'react'
import { toast } from 'react-toastify'

import { RESOLVE_PAYMENT_FOLLOW_UP_MUTATION } from '../../graphql/paymentFollowUps'
import { getTranslate } from '../../localization'
import { PaymentFollowUp } from '../../types/paymentFollowUps'
import EvidenceSummary from './EvidenceSummary'
import OutstandingActions from './OutstandingActions'
import SettlementFields from './SettlementFields'
import FollowUpAmount from './amount'
import useStyles from './index.styles'
import { followUpErrorMessage, reasonAction, reasonLabel } from './labels'
import {
  initialSettlement,
  projectionPending,
  scopedSettlement,
  settlementInput
} from './settlement'

export const RESOLUTION_MIN = 3
export const RESOLUTION_MAX = 500

const TITLE_ID = 'payment-follow-up-resolve-title'

/** Length in code points, as the BFF and adotel count it (a surrogate pair is one). */
export const resolutionLength = (text: string): number =>
  Array.from(text.trim()).length

interface Props {
  followUp: PaymentFollowUp
  onClose: () => void
  onResolved: () => void
}

export default function ResolveDialog({
  followUp,
  onClose,
  onResolved
}: Props) {
  const classes = useStyles()
  const [resolution, setResolution] = useState('')
  const [showError, setShowError] = useState(false)
  const [settlement, setSettlement] = useState(() =>
    initialSettlement(followUp)
  )
  const versioned = (followUp.caseSchemaVersion ?? 1) >= 2
  const structured = versioned ? settlementInput(followUp, settlement) : null
  const length = resolutionLength(resolution)
  const noteValid = length >= RESOLUTION_MIN && length <= RESOLUTION_MAX
  const valid = noteValid && (!versioned || structured !== null)
  const limits = { min: RESOLUTION_MIN, max: RESOLUTION_MAX }

  const [resolve, { loading }] = useMutation(
    RESOLVE_PAYMENT_FOLLOW_UP_MUTATION,
    {
      onCompleted: (data) => {
        toast.success(
          data.resolvePaymentFollowUp.state === 'Resolved'
            ? getTranslate('Follow-up resolved.')
            : getTranslate('Settlement recorded; review remains open.')
        )
        onResolved()
      },
      onError: (error) => toast.error(followUpErrorMessage(error))
    }
  )

  const save = () => {
    if (!valid) {
      setShowError(true)
      return
    }
    resolve({
      variables: {
        id: followUp.id,
        resolution: resolution.trim(),
        ...(versioned ? { settlement: structured } : {})
      }
    })
  }

  return (
    <Dialog
      open
      onClose={loading ? undefined : onClose}
      disableEscapeKeyDown={loading}
      disableBackdropClick={loading}
      aria-labelledby={TITLE_ID}
      fullWidth
      maxWidth="sm">
      <DialogTitle id={TITLE_ID}>
        {versioned
          ? getTranslate('Record settlement')
          : getTranslate('Resolve')}
        : {reasonLabel(followUp.reason)}
      </DialogTitle>
      <DialogContent>
        <div className={classes.summary}>
          <span className={classes.summaryLabel}>
            {getTranslate('Payment number')}
          </span>
          <bdi>{followUp.paymentNumber}</bdi>
          <span className={classes.summaryLabel}>
            {getTranslate('Reservation Number')}
          </span>
          <bdi>{followUp.displayReservationNumber ?? '-'}</bdi>
          <span className={classes.summaryLabel}>
            {getTranslate('Payment amount')}
          </span>
          <FollowUpAmount
            amount={followUp.amount}
            currency={followUp.currency}
          />
          <span className={classes.summaryLabel}>
            {getTranslate('Amount under review')}
          </span>
          {followUp.amountUnderReview == null ? (
            <span>{getTranslate('Needs reconciliation')}</span>
          ) : (
            <FollowUpAmount
              amount={followUp.amountUnderReview}
              currency={followUp.currency}
            />
          )}
          <span className={classes.summaryLabel}>{getTranslate('Reason')}</span>
          <span>
            {reasonAction(
              followUp.reason,
              followUp.gateway,
              scopedSettlement(followUp)
                ? settlement.action || undefined
                : followUp.requiredAction
            )}
          </span>
        </div>
        <Typography component="p" color="textSecondary">
          {getTranslate(
            'Resolving records an action already verified outside this page. It does not refund money, reverse an approval or credit a wallet. Record the settlement reference and verification in your note.'
          )}
        </Typography>
        <EvidenceSummary evidence={followUp.evidence} />
        {(scopedSettlement(followUp) || projectionPending(followUp)) && (
          <OutstandingActions followUp={followUp} />
        )}
        {versioned && (
          <SettlementFields
            followUp={followUp}
            value={settlement}
            onChange={setSettlement}
            disabled={loading}
            invalid={showError && structured === null}
          />
        )}
        <TextAreaWithLabel
          id="payment-follow-up-resolution"
          autoFocus
          fullWidth
          required
          rows={3}
          label={getTranslate('What was done?')}
          value={resolution}
          onChange={(event) => setResolution(event.currentTarget.value)}
          onBlur={() => resolution !== '' && setShowError(true)}
          error={showError && !noteValid}
          noHelperText
          maxLength={RESOLUTION_MAX}
          dir="auto"
          aria-invalid={showError && !noteValid}
          aria-describedby="payment-follow-up-resolution-help"
          disabled={loading}
        />
        <Typography
          id="payment-follow-up-resolution-help"
          variant="caption"
          component="p"
          color={showError && !noteValid ? 'error' : 'textSecondary'}
          role={showError && !noteValid ? 'alert' : undefined}>
          {showError && !noteValid
            ? getTranslate(
                'The resolution must be between {min} and {max} characters.',
                limits
              )
            : `${getTranslate(
                'Between {min} and {max} characters',
                limits
              )} · ${length}/${RESOLUTION_MAX}`}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {getTranslate('Cancel')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={loading || projectionPending(followUp)}
          onClick={save}>
          {loading ? (
            <CircularProgress
              size={16}
              role="status"
              aria-label={getTranslate('Loading')}
            />
          ) : (
            getTranslate('Save')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
